import { createApi } from '@reduxjs/toolkit/query/react';
import {
  EventBilling,
  BillingResponse,
  BillingsResponse,
  UserBillingOrganizerResponse,
  UserBillingOrganizerTransactionsResponse,
  UserBillingOrganizerWithdrawalResponse,
  OrganizerCostsResponse,
  RevShareResponse,
  WithdrawalPreview,
} from './models';
import { getQueryStringFromParams } from 'lib.shared/src/utils';
import { GetFiltersByEnumResponse } from 'lib.store/src/main/enums/models';
import { API_EVENT_TAGS } from 'lib.store/src/tags';
import { organizerEvents } from '../events/events.api';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import { useApiReq } from 'lib.hooks/src';
import { apiBaseUrl } from 'lib.shared/src/api';

export type BillingReportsFiltersParams = {
  dateFrom?: string;
  dateTo?: string;
  events?: string[];
  partners?: string[];
  group_by_partner?: boolean;
  gap?: string | number;
  page?: number;
};

export const buildBillingReportsQueryString = ({
  dateFrom,
  dateTo,
  partners,
  events,
  gap,
  group_by_partner,
}: BillingReportsFiltersParams): string => {
  const params = new URLSearchParams();

  const partnersQueryString = getQueryStringFromParams(partners, 'partners');
  const eventsQueryString = getQueryStringFromParams(events, 'events');

  if (dateFrom) {
    params.append('filters[date][from]', `${dateFrom}`);
  }

  if (dateTo) {
    params.append('filters[date][to]', `${dateTo}`);
  }

  if (gap) {
    params.append('filters[gap]', `${gap}`);
  }

  if (group_by_partner) {
    params.append('filters[group_by_partner]', `${group_by_partner ? 1 : 0}`);
  }

  const filterQueryString = params.toString();

  return [partnersQueryString, eventsQueryString, filterQueryString].filter(Boolean).join('&');
};

// const billing = `organizer_billing`;
// const post_billing = `organizer_post_billing`;

// const update_billing_by_id = `organizer_update_billing_by_id`;

// const events_setup_progress = `events_setup_progress`;

export const organizerBillingApi = createApi({
  reducerPath: API_EVENT_TAGS.ORGANIZER_BILLING,
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    API_EVENT_TAGS.ORGANIZER_BILLING,
    API_EVENT_TAGS.ORGANIZER_POST_BILLING,
    API_EVENT_TAGS.ORGANIZER_UPDATE_BILLING_BY_ID,
    API_EVENT_TAGS.EVENTS_SETUP_PROGRESS,
    API_EVENT_TAGS.ORGANIZER_EVENTS,
    API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID,
  ],
  endpoints: (build) => ({
    getUserOrganizerBilling: build.query<BillingsResponse, { page: number }>({
      query: ({ page }) => {
        return {
          url: `/organizer/billing?page=${page}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_BILLING, API_EVENT_TAGS.ORGANIZER_POST_BILLING],
    }),
    postBilling: build.mutation<{ data: EventBilling }, EventBilling>({
      query: (body) => ({
        body,
        method: 'POST',
        url: `/organizer/billing`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: () => [
        { type: API_EVENT_TAGS.ORGANIZER_POST_BILLING },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
      ],
    }),
    patchBillingById: build.mutation<{ data: EventBilling }, { data: EventBilling; id?: string }>({
      query: ({ data, id }) => ({
        url: `/organizer/billing/${id ? id.toString() : ''}`,
        method: 'PATCH',
        body: data,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [
        { type: API_EVENT_TAGS.ORGANIZER_POST_BILLING },
        { type: API_EVENT_TAGS.ORGANIZER_UPDATE_BILLING_BY_ID },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
      ],
    }),
    getBillingById: build.query<{ data: EventBilling }, { id: string }>({
      query: ({ id }) => ({
        url: `/organizer/billing/${id}`,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      providesTags: [
        API_EVENT_TAGS.ORGANIZER_BILLING,
        API_EVENT_TAGS.ORGANIZER_UPDATE_BILLING_BY_ID,
      ],
    }),
    getUserOrganizerBillingByEventId: build.query<BillingResponse, { eventId: string }>({
      query: ({ eventId }) => {
        return {
          url: `/organizer/billing/event/${eventId}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_BILLING],
    }),
    getUserOrganizerBillingReports: build.query<
      UserBillingOrganizerResponse,
      {
        dateFrom?: string;
        dateTo?: string;
        events?: string[];
        partners?: string[];
        group_by_partner?: boolean;
        gap?: string | number;
        page?: number;
        billingId: string;
      }
    >({
      query: ({ dateFrom, dateTo, partners, events, gap, group_by_partner, page, billingId }) => {
        const queryString = buildBillingReportsQueryString({
          dateFrom,
          dateTo,
          partners,
          events,
          gap,
          group_by_partner,
          page,
        });

        return {
          url: `/organizer/billing/${billingId}/reports${queryString.length ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_BILLING],
    }),
    getUserOrganizerBillingWithdrawals: build.query<
      UserBillingOrganizerWithdrawalResponse,
      {
        dateFrom?: string;
        dateTo?: string;
        status?: string[];
        page?: number;
        billingId: string;
        sortField?: string;
        sortDirection?: string;
      }
    >({
      query: ({ dateFrom, dateTo, status, page, billingId, sortField, sortDirection }) => {
        const params = new URLSearchParams();

        const statusQueryString = getQueryStringFromParams(status, 'status');

        if (dateFrom) {
          params.append('filters[date][from]', `${dateFrom}`);
        }

        if (dateTo) {
          params.append('filters[date][to]', `${dateTo}`);
        }

        if (page) {
          params.append('page', `${page}`);
        }

        if (sortField && sortDirection) {
          params.append('sort[0][field]', sortField);
          params.append('sort[0][direction]', sortDirection);
        }

        const filterQueryString = params.toString();

        const queryString = [statusQueryString, filterQueryString].filter(Boolean).join('&');

        return {
          url: `/organizer/billing/${billingId}/withdrawals${queryString.length ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_BILLING],
    }),
    getWithdrawalPreview: build.query<WithdrawalPreview, { amount: number; billingId: string }>({
      query: ({ amount, billingId }) => {
        const basePath = `/organizer/billing/${billingId}/withdrawals/preview`;
        const params = new URLSearchParams();
        params.append('amount', `${amount}`);
        const queryString = params.toString();

        return {
          url: `${basePath}${queryString.length ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      transformResponse: (response: { data: WithdrawalPreview }) => response.data,
      providesTags: [API_EVENT_TAGS.ORGANIZER_BILLING],
    }),
    postWithdrawal: build.mutation<void, { amount: number; billingId: string }>({
      query: ({ amount, billingId }) => ({
        url: `/organizer/billing/${billingId}/withdrawals`,
        method: 'POST',
        body: {
          amount,
        },
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: () => [
        { type: API_EVENT_TAGS.ORGANIZER_BILLING },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
      ],
    }),
    getUserOrganizerBillingTransactions: build.query<
      UserBillingOrganizerTransactionsResponse,
      {
        dateFrom?: string;
        dateTo?: string;
        types?: string[];
        events?: string[];
        page?: number;
        billingId: string;
        sortField?: string;
        sortDirection?: string;
      }
    >({
      query: ({ dateFrom, dateTo, types, events, page, billingId, sortField, sortDirection }) => {
        const params = new URLSearchParams();

        const typesQueryString = getQueryStringFromParams(types, 'types');
        const eventsQueryString = getQueryStringFromParams(events, 'events');

        if (dateFrom) {
          params.append('filters[date][from]', `${dateFrom}`);
        }

        if (dateTo) {
          params.append('filters[date][to]', `${dateTo}`);
        }

        if (page) {
          params.append('page', `${page}`);
        }

        if (sortField && sortDirection) {
          params.append('sort[0][field]', sortField);
          params.append('sort[0][direction]', sortDirection);
        }

        const filterQueryString = params.toString();

        const queryString = [typesQueryString, eventsQueryString, filterQueryString]
          .filter(Boolean)
          .join('&');

        return {
          url: `/organizer/billing/${billingId}/transactions${queryString.length ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_BILLING],
    }),
    getCurrenciesByISO: build.query<
      GetFiltersByEnumResponse,
      {
        iso: string;
      }
    >({
      query: ({ iso }) => {
        return {
          url: `/organizer/billing/currencies/${iso}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_BILLING],
    }),
    getOrganizerCostsByBillingId: build.query<OrganizerCostsResponse, { billingId: string }>({
      query: ({ billingId = '' }) => {
        return {
          url: `/organizer/billing/${billingId}/organizer-costs`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_BILLING],
    }),
    getRevShareCommissionsByEventId: build.query<
      RevShareResponse,
      { events: string[]; billingId: string }
    >({
      query: ({ events = [], billingId = '' }) => {
        const eventsQueryString = getQueryStringFromParams(events, 'events');
        const queryString = [eventsQueryString].filter(Boolean).join('&');
        return {
          url: `/organizer/billing/${billingId}/commissions${queryString.length ? '?' + queryString : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_BILLING],
    }),
    postChangeBilling: build.mutation<BillingResponse, { eventId: string; billingId: number }>({
      query: ({ eventId, billingId }) => {
        return {
          method: 'POST',
          url: `/organizer/events/${eventId}/setup/billing/${billingId}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Manually invalidate the event query in the organizerEvents slice
          dispatch(
            organizerEvents.util.invalidateTags([
              { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
            ]),
          );
        } catch {
          // If the query fails, we don't need to invalidate anything
        }
      },
      invalidatesTags: (_result, _error, { eventId: _eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_BILLING },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
      ],
    }),
  }),
});

export const {
  useGetUserOrganizerBillingQuery,
  useGetUserOrganizerBillingByEventIdQuery,
  useGetUserOrganizerBillingReportsQuery,
  useGetUserOrganizerBillingWithdrawalsQuery,
  useGetWithdrawalPreviewQuery,
  useLazyGetWithdrawalPreviewQuery,
  usePostWithdrawalMutation,
  useGetUserOrganizerBillingTransactionsQuery,
  useGetCurrenciesByISOQuery,
  usePostChangeBillingMutation,
  usePostBillingMutation,
  usePatchBillingByIdMutation,
  useGetBillingByIdQuery,
  useGetRevShareCommissionsByEventIdQuery,
  useGetOrganizerCostsByBillingIdQuery,
} = organizerBillingApi;

const getBillingReportsExportUrl = (billingId: string) => {
  return `${apiBaseUrl}/organizer/billing/${billingId}/reports/export`;
};

export const useExportBillingReports = (billingId: string) => {
  return useApiReq<Blob, BillingReportsFiltersParams>({
    reqConfig: {
      method: 'GET',
      url: getBillingReportsExportUrl(billingId),
      responseType: 'blob',
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      paramsSerializer: {
        serialize: (params) =>
          buildBillingReportsQueryString(params as BillingReportsFiltersParams),
      },
    },
  });
};
